import { type Island } from '@rta/rta-blueprint-library';
import { Box, Checkbox, Stack, Text } from '@chakra-ui/react';
import { type JSX } from 'react';

import type { ExtendedPanelRole, PanelThicknessOverrides } from '@rta/rta-blueprint-library';

import { InlineNumberInput } from '../controls';

export type PanelThicknessControlsProps = {
	island: Island;
	readonly: boolean;
	uniform: boolean;
	thicknessValues: PanelThicknessOverrides;
	onUniformToggle: (checked: boolean) => void;
	onUniformThicknessChange: (value: number) => void;
	onPanelThicknessChange: (role: ExtendedPanelRole, value: number) => void;
	onResetToDefault: () => void;
};

export const PanelThicknessControls = (props: PanelThicknessControlsProps): JSX.Element => {
	const {
		island,
		readonly,
		uniform,
		thicknessValues,
		onUniformToggle,
		onUniformThicknessChange,
		onPanelThicknessChange,
		onResetToDefault
	} = props;

	const uniformThickness = thicknessValues.front ?? island.surround?.getThicknessForRole('front') ?? 0.75;

	const independentInputs = island.isCornerIsland
		? <>
			<InlineNumberInput
				label="Front" min={0.25} step={0.0625}
				enabled={!readonly}
				value={thicknessValues.front ?? island.getThicknessForRole('front')}
				onChange={(v) => onPanelThicknessChange('front', v)}
			/>
			<InlineNumberInput
				label="Left" min={0.25} step={0.0625}
				enabled={!readonly}
				value={thicknessValues.left ?? island.getThicknessForRole('left')}
				onChange={(v) => onPanelThicknessChange('left', v)}
			/>
			<InlineNumberInput
				label="Right" min={0.25} step={0.0625}
				enabled={!readonly}
				value={thicknessValues.right ?? island.getThicknessForRole('right')}
				onChange={(v) => onPanelThicknessChange('right', v)}
			/>
			<InlineNumberInput
				label="Back Left" min={0.25} step={0.0625}
				enabled={!readonly}
				value={thicknessValues.backLeft ?? island.getThicknessForRole('backLeft')}
				onChange={(v) => onPanelThicknessChange('backLeft', v)}
			/>
			<InlineNumberInput
				label="Back Right" min={0.25} step={0.0625}
				enabled={!readonly}
				value={thicknessValues.backRight ?? island.getThicknessForRole('backRight')}
				onChange={(v) => onPanelThicknessChange('backRight', v)}
			/>
		</>
		: <>
			<InlineNumberInput
				label="Front" min={0.25} step={0.0625}
				enabled={!readonly}
				value={thicknessValues.front ?? island.getThicknessForRole('front')}
				onChange={(v) => onPanelThicknessChange('front', v)}
			/>
			<InlineNumberInput
				label="Back" min={0.25} step={0.0625}
				enabled={!readonly}
				value={thicknessValues.back ?? island.getThicknessForRole('back')}
				onChange={(v) => onPanelThicknessChange('back', v)}
			/>
			<InlineNumberInput
				label="Left" min={0.25} step={0.0625}
				enabled={!readonly}
				value={thicknessValues.left ?? island.getThicknessForRole('left')}
				onChange={(v) => onPanelThicknessChange('left', v)}
			/>
			<InlineNumberInput
				label="Right" min={0.25} step={0.0625}
				enabled={!readonly}
				value={thicknessValues.right ?? island.getThicknessForRole('right')}
				onChange={(v) => onPanelThicknessChange('right', v)}
			/>
		</>;

	return <Box mt="5px" padding="5px" textAlign="left" borderLeft="1px solid grey" borderBottom="1px solid darkgrey">
		<Stack spacing="8px">
			<Checkbox
				size="sm"
				isChecked={uniform}
				isDisabled={readonly}
				onChange={(e) => onUniformToggle(e.target.checked)}
			>
				<Text fontSize="small">Uniform</Text>
			</Checkbox>
			{ uniform
				? <InlineNumberInput
					label="Panel Thickness" min={0.25} step={0.0625}
					enabled={!readonly}
					value={uniformThickness}
					onChange={onUniformThicknessChange}
				/>
				: <Stack spacing="8px">
					{ independentInputs }
				</Stack>
			}
			<Box>
				<Text
					fontSize="x-small" color="blue.400" cursor="pointer"
					_hover={{ textDecoration: 'underline' }}
					onClick={onResetToDefault}
				>
					Reset to material default
				</Text>
			</Box>
		</Stack>
	</Box>;
};
